import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Col, Row } from 'antd';
import cs from 'classnames';

import Collapse from 'components/Collapse/Collapse';
import Education from 'components/EducationWorkExperience/components/Education';
import WorkExperience from 'components/EducationWorkExperience/components/WorkExperience';

import { useFetchEducationAndExperience } from 'hooks/useFetchEducationAndExperience';

import { FALLBACK_NUMBER_VALUE } from 'constants/uiSettings';

import styles from './EducationWorkExperience.module.scss';

interface EducationWorkExperienceProps {
  jobseekerId?: string | null;
  isCollapsed?: boolean;
}

const EducationWorkExperience: FC<EducationWorkExperienceProps> = ({
  jobseekerId,
  isCollapsed = false
}: EducationWorkExperienceProps) => {
  const { t } = useTranslation();
  const { data, isLoading } = useFetchEducationAndExperience({
    jobseekerId
  });

  const Wrapper = useMemo(() => {
    return ({
      children,
      maxStrings
    }: {
      children: ReactNode;
      maxStrings: number;
    }) =>
      isCollapsed ? (
        <Collapse maxStringsBeforeCollapse={maxStrings}>{children}</Collapse>
      ) : (
        <>{children}</>
      );
  }, [isCollapsed]);

  return (
    <>
      {isLoading ? (
        <div className={styles.loadingSpinner}>{t('common.loading')}</div>
      ) : (
        <>
          <div
            className={cs(
              styles.sectionContentWrapper,
              styles.sectionMarginBottom
            )}
          >
            <div className={styles.sectionHeader}>
              {t('common.educationWorkExperience.woTitle', {
                length:
                  data?.workExperience?.records?.length || FALLBACK_NUMBER_VALUE
              })}
              <div className={styles.divider} />
            </div>
            <Row className={styles.row}>
              <Col span={24}>
                {data?.workExperience?.records ? (
                  <Wrapper maxStrings={15}>
                    <WorkExperience workExperience={data.workExperience} />
                  </Wrapper>
                ) : (
                  <div className={styles.placeholder}>
                    {t('common.educationWorkExperience.woNoAvailable')}
                  </div>
                )}
              </Col>
            </Row>
          </div>

          <div
            className={cs(
              styles.sectionContentWrapper,
              styles.sectionMarginBottom
            )}
          >
            <div className={styles.sectionHeader}>
              {t('common.educationWorkExperience.edTitle', {
                length:
                  data?.education?.records?.length || FALLBACK_NUMBER_VALUE
              })}
              <div className={styles.divider} />
            </div>
            <Row className={styles.row}>
              <Col span={24}>
                {data?.education?.records ? (
                  <Wrapper maxStrings={10}>
                    <Education education={data?.education} />
                  </Wrapper>
                ) : (
                  <div className={styles.placeholder}>
                    {t('common.educationWorkExperience.edNoAvailable')}
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </>
      )}
    </>
  );
};

export default EducationWorkExperience;
